import { Modal, Button, Spinner } from "react-bootstrap";
import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAuthToken } from "../../../../util/auth";
import { getApiChara } from "../../../../util/getApi";
import ConfirmModal from "../../../UI/ConfirmModal";
import AuthContext from "../../../../store/auth-context";
import { printAdmissionA4 } from "./AdmissionPrint";
import Print from "./Print";
import { FaTrash, FaTimes, FaCheck, FaSave, FaFileExport, FaMicrophone } from "react-icons/fa";
import { useTranslation } from "react-i18next";

// Import modular components
import PatientDetails from "./PatientDetails";
import AdmissionDetails from "./AdmissionDetails";
import VitalSigns from "./VitalSigns";
import GuardianInformation from "./GuardianInformation";
import SymptomsDiagnosis from "./SymptomsDiagnosis";
import InsurancePayment from "./InsurancePayment";
import styles from "../NewIPDPatientAdmission.module.css";

const AdmissionDetailsModal = ({ show, onHide, admissionData, patientId }) => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const { hospitalData } = authCtx;
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [doctorsList, setDoctorsList] = useState([]);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const notesRef = useRef(null);

  // Initialize form data when admissionData changes
  useEffect(() => {
    if (admissionData) {
      setFormData({
        ...admissionData,
        // Ensure nested objects exist to avoid crashes in child components
        patientId: admissionData.patientId || {},
        wardBed: admissionData.wardBed || {},
        doctorId: admissionData.doctorId || {},
        // Handle potentially missing fields
        admissionDateTime: admissionData.admissionDateTime ? admissionData.admissionDateTime.replace(" ", "T").slice(0, 16) : "",
        tpaValidity: admissionData.tpaValidity ? admissionData.tpaValidity.slice(0, 10) : "",
        pho: admissionData.pho || "",
        co: admissionData.co || "",
        kco: admissionData.kco || "",
        cf: admissionData.cf || "",
        bp_systolic: (admissionData.bp && admissionData.bp.includes("/")) ? admissionData.bp.split("/")[0] : "",
        bp_diastolic: (admissionData.bp && admissionData.bp.includes("/")) ? admissionData.bp.split("/")[1] : "",
      });
    }
  }, [admissionData, show]);

  // Fetch dynamic data (doctors/beds)
  useEffect(() => {
    if (show) {
      const token = getAuthToken();
      if (!token || token === "EXPIRED") return;
      const apiChar = getApiChara();

      const fetchDoctors = async () => {
        try {
          const response = await fetch(`${apiChar}/users/getDoctorsList`, {
            headers: { Authorization: "Bearer " + token },
          });
          if (response.ok) setDoctorsList(await response.json());
        } catch (err) { console.error(err); }
      };

      const fetchBeds = async () => {
        try {
          const response = await fetch(`${apiChar}/ipd/wardBed/available`, {
            headers: { Authorization: "Bearer " + token },
          });
          if (response.ok) {
            const data = await response.json();
            // Important: If editing, the current bed might be marked as occupied. 
            // We should ensure it's in the list so the dropdown shows it.
            if (admissionData?.wardBed && !data.find(b => b.id === admissionData.wardBed.id)) {
              data.push(admissionData.wardBed);
            }
            setAvailableBeds(data || []);
          }
        } catch (err) { console.error(err); }
      };

      fetchDoctors();
      fetchBeds();
    }
  }, [show, admissionData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      if (child === "userId" && parent === "doctorId") {
        const selectedDr = doctorsList.find(dr => dr.userId === Number(value));
        setFormData(prev => ({ ...prev, doctorId: selectedDr || {} }));
      } else if (child === "id" && parent === "wardBed") {
        const selectedBed = availableBeds.find(bed => bed.id === Number(value));
        if (selectedBed) {
          setFormData(prev => ({ ...prev, wardBed: selectedBed }));
        } else {
          setFormData(prev => ({
            ...prev,
            wardBed: { ...prev.wardBed, id: 0, bedNumber: 0 }
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          [parent]: { ...prev[parent], [child]: value }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));

      // Scroll to notes if status is set to TRANSFERRED or CANCELLED
      if (name === "status" && (value === "TRANSFERRED" || value === "CANCELLED")) {
        setTimeout(() => {
          notesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          notesRef.current?.focus();
        }, 100);
      }
    }
  };

  const handleVoiceInput = () => {
    if (!("webkitSpeechRecognition" in window)) {
      toast.error(t("Speech recognition not supported in this browser."));
      return;
    }

    const SpeechRecognition = window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-IN";

    recognition.onstart = () => {
      setIsListening(true);
      toast.info(t("Listening... Speak now."));
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setFormData(prev => ({
        ...prev,
        notes: (prev.notes ? prev.notes + " " : "") + transcript
      }));
      toast.success(t("Voice input added!"));
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      console.error("Speech recognition error:", event.error);
      toast.error(t("Speech recognition error: ") + event.error);
    };

    recognition.onend = () => {
      setIsListening(false);
      toast.info(t("Stopped listening."));
    };

    recognition.start();
  };

  const pulseMicStyles = `
    @keyframes pulse-mic {
      0% { transform: scale(1); }
      50% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }
  `;

  const handleUpdate = async () => {
    const wardBedId = Number(formData.wardBed?.id || 0);
    if (!formData.wardBed?.floor || !formData.wardBed?.wardName || wardBedId === 0) {
      toast.error("Please select a valid Floor, Ward and Bed Number.");
      return;
    }
    if (!formData.doctorId?.userId) {
      toast.error("Please select a Consultant Doctor.");
      return;
    }
    if (!formData.admissionReason) {
      toast.error("Admission Reason is required.");
      return;
    }

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const token = getAuthToken();
      // Use PUT with ID as parameter
      const apiUrl = `${apiChar}/ipd/admissions/${formData.ipdAdmissionId}`;

      const formatDateTime = (val) => {
        if (!val) return null;
        if (val.length === 16) return val.replace("T", " ") + ":00";
        return val.replace("T", " ").replace(/\..*$/, "").replace("Z", "");
      };

      const { bp_systolic, bp_diastolic, ...filteredFormData } = formData;
      const combinedBP = (bp_systolic || bp_diastolic) ? `${bp_systolic || "0"}/${bp_diastolic || "0"}` : "";

      const payload = {
        ...filteredFormData,
        bp: combinedBP,
        admissionDateTime: formatDateTime(formData.admissionDateTime),
        tpaValidity: formData.tpaValidity || null,
      };

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Failed to update admission");
      toast.success(t("Admission updated successfully!"));
      onHide(); // Close modal on success
    } catch (err) {
      console.error(err);
      toast.error(t("Error updating admission: ") + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const token = getAuthToken();
      const apiUrl = `${apiChar}/ipd/admissions/${formData.ipdAdmissionId}`;

      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Deletion failed");
      toast.success(t("Admission deleted successfully!"));
      setShowDeleteConfirm(false);
      onHide();
      navigate("/dashboard/ipd");
    } catch (err) {
      console.error(err);
      toast.error(t("Delete failed: ") + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!formData) return null;
  const isDischarged = formData.status !== 'ACTIVE';

  return (
    <>
      <style>
        {`
          .history-backdrop {
            background-color: rgba(var(--primary-rgb), 0.5) !important;
            backdrop-filter: blur(5px);
          }
          .dimmed-modal-content {
            opacity: 0.2 !important;
            filter: blur(3px) grayscale(50%);
            transition: all 0.4s ease-in-out;
            pointer-events: none;
          }
          .dimmed-modal-dialog {
            transform: scale(0.97) !important;
            transition: all 0.4s ease-in-out;
          }
            @keyframes pulse-mic {
        `}
      </style>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        backdrop="static"
        contentClassName={showDeleteConfirm ? "dimmed-modal-content" : ""}
        dialogClassName={showDeleteConfirm ? "dimmed-modal-dialog" : ""}
      >
        <style>{pulseMicStyles}</style>
        <Modal.Header closeButton className="modal-header-themed">
          <Modal.Title>{t("Edit Admission Details")} - {formData.patientId?.firstName} {formData.patientId?.lastName}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          <div className={styles.formWrapper} style={{ boxShadow: "none", borderRadius: 0 }}>
            <PatientDetails formData={formData} />
            <div className={styles.mainForm}>
              <AdmissionDetails
                formData={formData}
                handleInputChange={handleInputChange}
                doctorsList={doctorsList}
                availableBeds={availableBeds}
              />
              <VitalSigns formData={formData} handleInputChange={handleInputChange} />
              <GuardianInformation formData={formData} handleInputChange={handleInputChange} />
              <SymptomsDiagnosis formData={formData} handleInputChange={handleInputChange} />
              <InsurancePayment formData={formData} handleInputChange={handleInputChange} />

              <div className={styles.section}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h3 className="m-0">{t("Admission Notes / Remarks")}</h3>
                  <FaMicrophone
                    onClick={handleVoiceInput}
                    style={{
                      cursor: "pointer",
                      fontSize: "1.2rem",
                      color: isListening ? "#f44336" : "#25d366",
                      animation: isListening ? "pulse-mic 1s infinite" : "none"
                    }}
                    title={t("Voice Input")}
                  />
                </div>
                <textarea
                  ref={notesRef}
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder={t("Enter remarks... (Please specify reason for Transfer/Cancellation if applicable)")}
                />
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4 flex-wrap">
                <Button 
                  className="btn-danger-premium" 
                  onClick={() => setShowDeleteConfirm(true)} 
                  disabled={isLoading || isDischarged}
                >
                  <FaTrash className="me-2" /> {t("Delete")}
                </Button>
                <div style={{ flex: 1 }} className="d-none d-md-block"></div>

                <Print formData={formData} hospitalData={hospitalData} />
                <Button
                  className="btn-export-premium"
                  onClick={() => printAdmissionA4(formData, authCtx)}
                >
                  <FaFileExport className="me-2" /> {t("Export")}
                </Button>

                <Button className="btn-secondary-premium" onClick={onHide} disabled={isLoading}>
                  <FaTimes className="me-2" /> {t("Close")}
                </Button>
                 <Button 
                  className="btn-premium" 
                  onClick={handleUpdate} 
                  disabled={isLoading || isDischarged}
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> {t("Update")}</>}
                </Button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t("Confirm Delete")}
        message={
          <>
            <p className="mb-2">{t("Are you sure you want to delete the admission for")} <strong>{formData.patientId?.firstName} {formData.patientId?.lastName}</strong>?</p>
          </>
        }
        variant="danger"
        isLoading={isLoading}
      />

    </>
  );
};

export default AdmissionDetailsModal;

